import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500); // Show loading for 2.5 seconds

    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            overflow: 'hidden'
          }}
        >
          {/* Animated Background Particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 0,
                scale: 0,
                x: Math.random() * 100 - 50,
                y: Math.random() * 100 - 50
              }}
              animate={{
                opacity: [0, 0.3, 0],
                scale: [0, Math.random() * 2 + 1, 0],
                x: Math.random() * 200 - 100,
                y: Math.random() * 200 - 100
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
              style={{
                position: 'absolute',
                width: Math.random() * 4 + 2,
                height: Math.random() * 4 + 2,
                backgroundColor: '#034522',
                borderRadius: '50%',
                filter: 'blur(1px)'
              }}
            />
          ))}

          {/* Logo Animation */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              duration: 1.2,
              ease: "easeOut",
              type: "spring"
            }}
            exit={{ scale: 0, rotate: 180, opacity: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <motion.img
                src="/logo.png"
                alt="GrowAITech Logo"
                style={{ 
                  height: '70px', 
                  width: 'auto',
                  filter: 'drop-shadow(0 0 20px rgba(26, 158, 127, 0.5))'
                }}
                animate={{
                  rotateY: [0, 360],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </Box>
          </motion.div>

          {/* Company Name with Typing Effect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: '#034522',
                fontSize: { xs: '2rem', md: '3rem' },
                letterSpacing: '-0.5px',
                textShadow: '0 0 30px rgba(3, 69, 34, 0.1)'
              }}
            >
              <span style={{ color: '#1A9E7F' }}>G</span>row
              <span style={{ color: '#1A9E7F' }}>A</span>I
              <span style={{ color: '#1A9E7F' }}>T</span>ech
            </Typography>
          </motion.div>

          {/* Loading Bar */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 200, opacity: 1 }}
            transition={{ delay: 0.8, duration: 1.5 }}
            exit={{ width: 0, opacity: 0 }}
            style={{
              height: '4px',
              backgroundColor: '#034522',
              borderRadius: '2px',
              marginTop: '2rem',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <motion.div
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(3, 69, 34, 0.4), transparent)'
              }}
            />
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            exit={{ opacity: 0 }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(3, 69, 34, 0.7)',
                marginTop: '1rem',
                fontSize: '0.9rem',
                letterSpacing: '2px',
                textTransform: 'uppercase'
              }}
            >
              Initializing Excellence
            </Typography>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
